import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';

import pluralize from 'pluralize';

import ModalWindow from '@editor2/components/ModalWindow';
import { type MCQuestionPool, ElementType } from '@editor2/types/pageElements';

import { generateQuestionsModalStyles } from './styles';
import { type RootState } from '@editor2/reducers/root';
import { generateMCQPQuestions } from '@editor2/reducers/editor/effects';
import { parseHTMLTextBody } from '@editor2/services/htmlService';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type Entity } from '@editor2/types/entity';
import { type Page } from '@editor2/types/page';

interface Props {
	isShown: boolean;
	onHide: () => void;
	element: MCQuestionPool;
	courseId: number;
	pageId: string;
}

const GenerateQuestionsModal: FC<Props> = (props) => {
	const { isShown, onHide, element, courseId, pageId } = props;

	const [payload, setPayload] = useState({
		questionsCount: 3,
		choicesCount: 3,
		learningObjective: element.metadata['LO'] || '',
		questionType: 'Multiple'
	});

	const { pages, elements } = useSelector((state: RootState) => state.courses[courseId]);

	const dispatch = useDispatch();

	const onGenerateQuestionsClick = () => {
		const priorContent = getPriorContent({
			pages,
			elements,
			elementId: element.family_id,
			pageId
		});
		dispatch(
			generateMCQPQuestions({
				payload: {
					learning_objective: payload.learningObjective,
					prior_content: priorContent,
					questions_count: payload.questionsCount,
					choices_count: payload.choicesCount,
					question_type: payload.questionType
				},
				courseId,
				elementId: element.family_id
			})
		);

		// populate element's metadata with the learning objective if not present
		if (!element.metadata['LO']) {
			dispatch(
				updateElement({
					elementId: element.family_id,
					courseId,
					metadata: { ...element.metadata, LO: payload.learningObjective }
				})
			);
		}

		onHide();
	};

	const handleChange = (value) => {
		setPayload({ ...payload, ...value });
	};

	const title = <div>Generate questions</div>;

	const footer = (
		<>
			<div className="infoMesage">
				Note: You can continue to make edits while questions are generating in the background.
				You’ll receive a message when questions are done.
			</div>
			<Button
				variant="success"
				size="sm"
				disabled={!payload.learningObjective}
				onClick={onGenerateQuestionsClick}>
				Generate
			</Button>
		</>
	);

	return (
		<div onClick={(e) => e.stopPropagation()}>
			<ModalWindow
				isShown={isShown}
				onHide={onHide}
				title={title}
				footer={footer}
				modalStyles={generateQuestionsModalStyles}>
				<Form.Group className="countBlock">
					<Form.Label>How many questions would you like to generate?</Form.Label>
					<Form.Control
						type="number"
						step={1}
						min={1}
						max={5}
						value={payload.questionsCount}
						onChange={(e) => {
							const value = Number(e.target.value);
							handleChange({
								questionsCount: value > 5 ? 5 : value < 1 ? 1 : value
							});
						}}
					/>
				</Form.Group>

				<Form.Group className="countBlock">
					<Form.Label>How many choices for each question would you like to generate?</Form.Label>
					<Form.Control
						type="number"
						step={1}
						min={1}
						max={5}
						value={payload.choicesCount}
						onChange={(e) => {
							const value = Number(e.target.value);
							handleChange({
								choicesCount: value > 5 ? 5 : value < 1 ? 1 : value
							});
						}}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Choose the type of question(s) to generate:</Form.Label>
					<Form.Control
						as="select"
						value={payload.questionType}
						onChange={(e) => handleChange({ questionType: e.target.value })}>
						<option value="Multiple">Same question, multiple versions</option>
						<option value="Scenario">Scenario</option>
						<option value="MCAS">Multiple correct answer set</option>
					</Form.Control>
				</Form.Group>

				<Form.Group>
					<Form.Label>Provide the learning objective:</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						value={payload.learningObjective}
						onChange={(e) => handleChange({ learningObjective: e.target.value })}
					/>
				</Form.Group>
			</ModalWindow>
		</div>
	);
};

export default GenerateQuestionsModal;

interface GetPriorContentParams {
	pages: Entity<Page>;
	elements: {
		entities: {
			[id: string]: {
				type: ElementType;
				// other properties as needed
			};
		};
	};
	elementId: string;
	pageId: string;
}

const getPriorContent = ({ pages, elements, elementId, pageId }: GetPriorContentParams) => {
	const currentPageElements = pages.entities[pageId].elements;
	const index = currentPageElements.indexOf(elementId);
	const priorElementsIds = currentPageElements.slice(0, index);

	const priorTextElements = priorElementsIds
		.map((id) => elements.entities[id])
		.filter((el) => el.type === ElementType.HTMLText);

	if (priorTextElements.length) {
		return priorTextElements.map(parseHTMLTextBody).join('\n');
	}

	return null;
};
